import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import { get } from '@/api/client';
import { Skeleton } from '@/components/atoms/Skeleton';
import { Badge } from '@/components/atoms/Badge';
import { SEOHead } from '@/components/atoms/SEOHead';
import { BoardTabs } from '@/components/organisms/BoardTabs';
import { MARKET_REPORT_CATEGORIES, type MarketReportCategoryKey } from '@/constants/boards';
import { colorizePercent } from '@/lib/colorize';
import { cn } from '@/lib/cn';

interface MarketReport {
  id: number;
  category: MarketReportCategoryKey;
  title: string;
  summary: string | null;
  report_date: string;
  created_at: string;
  post_id: number | null;
}

interface ListResponse {
  reports: MarketReport[];
  total: number;
  page: number;
  limit: number;
  kst_today: string;
}

/** 카테고리 슬롯 정의 — 사용자 지정 순서: 모닝/코인/장중/국장/미장 (시간순 발생 순서) */
const TODAY_SLOT_ORDER: MarketReportCategoryKey[] = [
  'morning_brief',
  'coin_24h',
  'intraday',
  'kr_close',
  'us_close',
];

/** 슬롯별 노출 라벨 오버라이드 — 미장은 '전일 미장마감' 으로 표기.
 *  나머지는 MARKET_REPORT_CATEGORIES.label 사용. */
const SLOT_LABEL_OVERRIDE: Partial<Record<MarketReportCategoryKey, string>> = {
  us_close: '전일 미장마감',
};

/** 카테고리 메타 lookup */
function getCatMeta(key: MarketReportCategoryKey) {
  return MARKET_REPORT_CATEGORIES.find((c) => c.key === key);
}

/** Date(ISO) → KST yyyy-mm-dd */
function fmtDateKST(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  return kst.toISOString().slice(0, 10);
}

/** 미장 발행일 (KST) → 미국 ET 거래일 윈도우 'MM/DD ~ MM/DD'.
 *  ET 거래 마감은 KST 익일 새벽이라 ET 일자 = KST 발행일 -1.
 *  미국 시장은 ET 09:30~16:00 (KST 23:30~익일 06:00) — KST 기준으로 (전일 ~ 발행일) 형태가 자연. */
function fmtUsWindowMD(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const kst = new Date(d.getTime() + 9 * 60 * 60 * 1000);
  const prev = new Date(kst.getTime() - 24 * 60 * 60 * 1000);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(prev.getUTCMonth() + 1)}/${pad(prev.getUTCDate())} ~ ${pad(kst.getUTCMonth() + 1)}/${pad(kst.getUTCDate())}`;
}

export function MarketReportsPage() {
  // 오늘 데이터
  const todayQ = useQuery({
    queryKey: ['market-reports', 'today'],
    queryFn: () => get<ListResponse>('/market-reports?date=today&limit=20'),
    staleTime: 60_000,
  });

  // 이전 기록 (오늘 제외) — 미장(us_close) 의 latest 도 여기 포함됨
  const prevQ = useQuery({
    queryKey: ['market-reports', 'history'],
    queryFn: () => get<ListResponse>('/market-reports?exclude_today=true&limit=200'),
    staleTime: 5 * 60_000,
  });

  // 미장 — 오늘/과거 무관 가장 최근 1건 (전일 미장마감 슬롯 전용)
  const usLatestQ = useQuery({
    queryKey: ['market-reports', 'latest', 'us_close'],
    queryFn: () =>
      get<{ report: MarketReport }>('/market-reports/latest/us_close').catch(() => null),
    staleTime: 60_000,
  });

  const isLoading = todayQ.isLoading || prevQ.isLoading;
  const kstToday = todayQ.data?.kst_today ?? prevQ.data?.kst_today ?? '';
  const todayReports = todayQ.data?.reports ?? [];
  const prevReports = prevQ.data?.reports ?? [];
  const usLatest = usLatestQ.data?.report ?? null;

  // 오늘 5슬롯 매핑.
  // us_close 는 latest 의 report_date 가 오늘일 때만 오늘 슬롯에 노출 (이전엔 무조건 latest
  // 를 노출해 04:10 cron 발행 전에도 historical 글이 '오늘'처럼 보이는 혼동 발생).
  const usLatestIsToday =
    !!usLatest && fmtDateKST(usLatest.report_date) === kstToday;
  const todayByCat: Partial<Record<MarketReportCategoryKey, MarketReport>> = {};
  for (const r of todayReports) todayByCat[r.category] = r;
  if (usLatestIsToday) todayByCat.us_close = usLatest!;

  // 이전 기록 — 날짜별 그룹 (KST 기준 yyyy-mm-dd).
  // us_close latest 가 오늘에 노출된 경우에만 중복 회피 — 그 외엔 이전 기록에 그대로 포함.
  const grouped: Record<string, MarketReport[]> = {};
  for (const r of prevReports) {
    if (r.category === 'us_close' && usLatestIsToday && r.id === usLatest!.id) continue;
    const day = fmtDateKST(r.report_date);
    if (!grouped[day]) grouped[day] = [];
    grouped[day].push(r);
  }
  // 각 날짜 그룹 내부도 슬롯 순서대로 정렬
  const slotOrderIndex = new Map(TODAY_SLOT_ORDER.map((k, i) => [k, i]));
  for (const list of Object.values(grouped)) {
    list.sort(
      (a, b) =>
        (slotOrderIndex.get(a.category) ?? 99) - (slotOrderIndex.get(b.category) ?? 99),
    );
  }
  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  return (
    <>
      <SEOHead
        title="시장상황 — 시세공간"
        description="오늘의 국장·미장·코인 마감 정리"
      />
      <BoardTabs activeSlug="market" />
      <div className="container-base pb-6">
        <header className="mb-6">
          <h1 className="text-3xl font-extrabold">시장상황</h1>
          <p className="mt-2 text-sm text-[var(--color-text-sub)]">
            오늘의 국장·미장·코인 마감 정리
          </p>
        </header>

        {/* ─── 오늘 ─── */}
        <section className="mb-10">
          <SectionHeader title={`오늘 ${kstToday || '...'}`} />
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14" />
              ))}
            </div>
          ) : (
            <ul className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]">
              {TODAY_SLOT_ORDER.map((cat, i) => {
                const meta = getCatMeta(cat);
                const r = todayByCat[cat];
                const pending = !r;
                const label = SLOT_LABEL_OVERRIDE[cat] ?? meta?.label ?? cat;
                // 미장은 latest 1건 노출이라 어느 미국 거래일 데이터인지 '(전일 ~ 발행일 미장)' 으로 표기
                // 예: KST 발행 05/12 → 미국 ET 05/11 정규장 → '(05/11 ~ 05/12 미장)'
                const titleOverride =
                  cat === 'us_close' && r ? `(${fmtUsWindowMD(r.report_date)} 미장) · ${r.title}` : null;
                return (
                  <SlotRow
                    key={cat}
                    isFirst={i === 0}
                    catLabel={label}
                    timeLabel={meta?.timeLabel ?? ''}
                    pending={pending}
                    title={titleOverride ?? r?.title ?? null}
                    target={r ? `/market/${r.id}` : null}
                  />
                );
              })}
            </ul>
          )}
        </section>

        {/* ─── 이전 기록 ─── */}
        <section>
          <SectionHeader title="이전 기록" />
          {isLoading ? (
            <div className="space-y-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-14" />
              ))}
            </div>
          ) : sortedDates.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] py-12 text-center text-sm text-[var(--color-text-sub)]">
              이전 기록이 없어요.
            </div>
          ) : (
            <div className="space-y-6">
              {sortedDates.map((day) => {
                const list = grouped[day];
                return (
                  <div key={day}>
                    <h3 className="mb-2 text-sm font-semibold text-[var(--color-text-sub)]">
                      {day}
                    </h3>
                    <ul className="overflow-hidden rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)]">
                      {list.map((r, i) => {
                        const meta = getCatMeta(r.category);
                        return (
                          <SlotRow
                            key={r.id}
                            isFirst={i === 0}
                            catLabel={SLOT_LABEL_OVERRIDE[r.category] ?? meta?.label ?? r.category}
                            timeLabel={meta?.timeLabel ?? ''}
                            pending={false}
                            title={r.title}
                            target={`/market/${r.id}`}
                          />
                        );
                      })}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </>
  );
}

/* ──────────────────────────────────────────────────────────── */

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="mb-3 flex items-center gap-3">
      <h2 className="text-lg font-bold">{title}</h2>
      <div className="h-px flex-1 bg-[var(--color-border)]" />
    </div>
  );
}

/** 한 줄 row — 카테고리 / 발행시간(예정) / 제목(여러 줄).
 *  카드 내부 날짜/작성시간/chevron 은 모두 제거 — 섹션 헤더의 날짜만 단일 출처. */
function SlotRow({
  isFirst,
  catLabel,
  timeLabel,
  pending,
  title,
  target,
}: {
  isFirst: boolean;
  catLabel: string;
  timeLabel: string;
  pending: boolean;
  title: string | null;
  target: string | null;
}) {
  // ' · ' 기준 분할 후 각 부분을 줄바꿈 (KOSPI / KOSDAQ 같이 지수별 한 줄씩).
  // MM/DD 형식(예: '05/12')은 카드에서 노출 안 함 — 섹션 헤더의 날짜만 단일 출처.
  const titleLines = title
    ? title
        .split(' · ')
        .map((s) => s.trim())
        .filter((s) => s && !/^\d{2}\/\d{2}$/.test(s))
    : [];

  const inner = (
    <div
      className={cn(
        'flex items-start gap-3 px-4 py-3',
        !isFirst && 'border-t border-[var(--color-border)]',
        pending
          ? 'bg-[var(--color-surface)] opacity-60'
          : 'transition-colors hover:bg-[var(--color-bg)]',
      )}
    >
      {/* 카테고리 */}
      <div className="w-24 shrink-0 sm:w-32">
        <Badge variant={pending ? 'default' : 'primary'}>{catLabel}</Badge>
      </div>
      {/* 발행시간 (예정 시각) */}
      <div className="hidden w-20 shrink-0 items-center gap-1 pt-1 text-xs text-[var(--color-text-sub)] sm:flex">
        <Clock className="h-3.5 w-3.5" />
        {timeLabel}
      </div>
      {/* 제목 — 여러 줄 */}
      <div className="min-w-0 flex-1">
        {pending ? (
          <span className="text-sm text-[var(--color-text-sub)]">발행 대기</span>
        ) : titleLines.length > 0 ? (
          <div className="space-y-0.5 text-sm leading-snug">
            {titleLines.map((line, i) => (
              <div key={i} className="break-words">
                {colorizePercent(line)}
              </div>
            ))}
          </div>
        ) : (
          <span className="text-sm">-</span>
        )}
      </div>
    </div>
  );
  if (pending || !target) {
    return <li>{inner}</li>;
  }
  return (
    <li>
      <Link to={target} className="block">
        {inner}
      </Link>
    </li>
  );
}
